import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { registerService } from '@shared/services/register';
import LoadingButton from '@shared/components/LoadingButton';
import useFormHook from '@shared/hooks/useFormHook';
import { useState } from 'react';
import Select from 'react-select';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useSelector } from 'react-redux';

export default function RegisterForm({ onClose, onConfirm }) {
    const { t } = useTranslation();
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    const { register, handleSubmit, errors, watch, setValue, clearErrors, formState: { loading, fetchingData } } = useForm();
    const { modalRef } = useFormHook({}, onClose);
    const [personsByIdn, setPersonsByIdn] = useState([]);
    const [personsByName, setPersonsByName] = useState([]);
    const [selectedPersonByIdn, setSelectedPersonByIdn] = useState(null);
    const [selectedPersonByName, setSelectedPersonByName] = useState(null);
    const [isLoadingByIdn, setIsLoadingByIdn] = useState(false);
    const [isLoadingByName, setIsLoadingByName] = useState(false);

    const searchPersonsByIdn = async (searchInput) => {
        if (!searchInput || searchInput.length < 2) {
            setPersonsByIdn([]);
            return;
        }
        try {
            setIsLoadingByIdn(true);
            const response = await registerService.searchPersons({ idn: searchInput });
            const personsList = response._embedded?.personsDTOList || response.content || (Array.isArray(response) ? response : []);
            const personOptions = personsList.map(person => {
                const idnValue = person.idn?.idn || person.idn || '';
                const fullName = `${person.name || ''} ${person.fathersName || ''} ${person.surname || ''}`.trim();
                return {
                    value: person.id,
                    label: `${fullName} (${idnValue})`,
                    idn: idnValue,
                    person: person
                };
            });
            setPersonsByIdn(personOptions);
        } catch (error) {
            console.error('Error searching persons by idn:', error);
            setPersonsByIdn([]);
        } finally {
            setIsLoadingByIdn(false);
        }
    };

    const searchPersonsByName = async (searchInput) => {
        if (!searchInput || searchInput.length < 2) {
            setPersonsByName([]);
            return;
        }
        try {
            setIsLoadingByName(true);
            const searchTerms = searchInput.trim().split(/\s+/);
            let searchParams = {};
            if (searchTerms.length === 1) {
                searchParams.name = searchTerms[0];
            } else if (searchTerms.length === 2) {
                searchParams.name = searchTerms[0];
                searchParams.fathersName = searchTerms[1];
            } else if (searchTerms.length >= 3) {
                searchParams.name = searchTerms[0];
                searchParams.fathersName = searchTerms[1];
                searchParams.surname = searchTerms[2];
            }
            const response = await registerService.searchPersons(searchParams);
            const personsList = response._embedded?.personsDTOList || response.content || (Array.isArray(response) ? response : []);
            const personOptions = personsList.map(person => {
                const idnValue = person.idn?.idn || person.idn || '';
                const fullName = `${person.name || ''} ${person.fathersName || ''} ${person.surname || ''}`.trim();
                return {
                    value: person.id,
                    label: fullName,
                    idn: idnValue,
                    person: person
                };
            });
            setPersonsByName(personOptions);
        } catch (error) {
            console.error('Error searching persons by name:', error);
            setPersonsByName([]);
        } finally {
            setIsLoadingByName(false);
        }
    };

    const debouncedSearchByIdn = useDebounce(searchPersonsByIdn, 1000);
    const debouncedSearchByName = useDebounce(searchPersonsByName, 1000);

    const handlePersonByIdnSelection = (selectedOption) => {
        setSelectedPersonByIdn(selectedOption);
        if (selectedOption && selectedOption.person) {
            const person = selectedOption.person;
            const idnValue = person.idn?.idn || person.idn || '';
            const fullName = `${person.name || ''} ${person.fathersName || ''} ${person.surname || ''}`.trim();
            
            setValue('idn', idnValue);
            setValue('employeeName', fullName);
            setValue('citizenship', person.nationality || '');
            
            // Also update the name select to show the selected person
            setSelectedPersonByName({
                value: person.id,
                label: fullName,
                idn: idnValue,
                person: person
            });
        } else {
            setValue('idn', '');
            setValue('employeeName', '');
            setValue('citizenship', '');
            setSelectedPersonByName(null);
        }
    };

    const handlePersonByNameSelection = (selectedOption) => {
        setSelectedPersonByName(selectedOption);
        if (selectedOption && selectedOption.person) {
            const person = selectedOption.person;
            const idnValue = person.idn?.idn || person.idn || '';
            const fullName = `${person.name || ''} ${person.fathersName || ''} ${person.surname || ''}`.trim();
            
            setValue('idn', idnValue);
            setValue('employeeName', fullName);
            setValue('citizenship', person.nationality || '');
            
            // Also update the idn select to show the selected person
            setSelectedPersonByIdn({
                value: person.id,
                label: `${fullName} (${idnValue})`,
                idn: idnValue,
                person: person
            });
        } else {
            setValue('idn', '');
            setValue('employeeName', '');
            setValue('citizenship', '');
            setSelectedPersonByIdn(null);
        }
    };

    const submitForm = async (data) => {
        const personId = selectedPersonByIdn?.value || selectedPersonByName?.value;
        const payload = {
            personId,
            branchId: adminBranchId
        }
        if (!personId) {
            return;
        }
        if (onConfirm) {
            await onConfirm(payload);
        }
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <div className="modal-body">
                        <h4 className="modal-title">{t('staff.register.addRegister')}</h4>
                    </div>
                    <form id="registerForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                        <div className="form-sections"  style={{ display: 'flex', justifyContent: 'center' }}>
                            <div className="form-section" style={{ maxWidth: '500px', width: '100%' }}>
                                <div className="form-group">
                                    <span>{t('staff.register.table.headers.idn')}<span className="required">*</span></span>
                                    <Select
                                        name='idn'
                                        placeholder={t('users.table.search.startType')}
                                        options={personsByIdn}
                                        value={selectedPersonByIdn}
                                        onChange={handlePersonByIdnSelection}
                                        onInputChange={(inputValue) => {
                                            if (inputValue && inputValue.length >= 2) {
                                                debouncedSearchByIdn(inputValue);
                                            } else {
                                                setPersonsByIdn([]);
                                            }
                                            return inputValue;
                                        }}
                                        isLoading={isLoadingByIdn}
                                        isSearchable={true}
                                        isClearable={true}
                                        noOptionsMessage={() => t('common.select')}
                                        loadingMessage={() => t('common.loading')}
                                    />
                                    <input type="hidden" {...register('idn')} />
                                </div>
                                <div className="form-group">
                                    <span>{t('staff.register.table.headers.employeeName')}<span className="required">*</span></span>
                                    <Select
                                        name='employeeName'
                                        placeholder={t('users.table.search.startType')}
                                        options={personsByName}
                                        value={selectedPersonByName}
                                        onChange={handlePersonByNameSelection}
                                        onInputChange={(inputValue) => {
                                            if (inputValue && inputValue.length >= 2) {
                                                debouncedSearchByName(inputValue);
                                            } else {
                                                setPersonsByName([]);
                                            }
                                            return inputValue;
                                        }}
                                        isLoading={isLoadingByName}
                                        isSearchable={true}
                                        isClearable={true}
                                        noOptionsMessage={() => t('common.select')}
                                        loadingMessage={() => t('common.loading')}
                                    />
                                    <input type="hidden" {...register('employeeName')} />
                                </div>
                                <div className="form-group">
                                    <span>{t('staff.register.table.headers.citizenship')}</span>
                                    <input type="text" className="form-control" {...register('citizenship')} />
                                </div>
                            </div>
                        </div>
                    </form>
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="registerForm" className="btn btn-primary" loading={loading} disabled={fetchingData}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}